import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';

const TermsOfService = () => {
  const lastUpdated = 'May 13, 2026';

  const sections = [
    {
      title: 'Acceptance of Terms',
      content: [
        {
          text: 'By accessing or using the Z37 website and services, you agree to be bound by these Terms of Service. If you do not agree to these terms, please do not use our website or services.'
        }
      ]
    },
    {
      title: 'Description of Services',
      content: [
        {
          text: 'Z37 LLC provides AI-powered consultancy services, including but not limited to strategy consulting, AI/ML solution development, data analytics, and related advisory services. Our website includes an AI-powered discovery chat and wireframe generation tool that processes your input through third-party AI services (Google Gemini). The specific services provided to clients are governed by separate service agreements.'
        }
      ]
    },
    {
      title: 'Use of Website',
      content: [
        {
          text: 'You agree to use our website only for lawful purposes and in a manner that does not infringe upon or restrict anyone else\'s use of the website. Prohibited activities include:'
        },
        {
          list: [
            'Attempting to gain unauthorized access to our systems or networks',
            'Transmitting malicious code or interfering with website functionality',
            'Using the website to distribute unsolicited commercial content',
            'Impersonating any person or entity',
            'Collecting personal information about other users without consent'
          ]
        }
      ]
    },
    {
      title: 'Intellectual Property',
      content: [
        {
          text: 'All content on this website, including text, graphics, logos, images, and software, is the property of Z37 or its content suppliers and is protected by United States and international copyright laws.'
        },
        {
          text: 'You may not reproduce, distribute, modify, or create derivative works from any content on this website without our express written permission.'
        }
      ]
    },
    {
      title: 'AI and Consulting Services Disclaimer',
      content: [
        {
          text: 'Our AI and consulting services are provided on an "as is" basis. While we strive for accuracy and effectiveness:'
        },
        {
          list: [
            'AI models and predictions are probabilistic and may not be 100% accurate',
            'Past performance does not guarantee future results',
            'Our recommendations are advisory in nature and should be validated by your team',
            'We do not guarantee specific business outcomes from our services',
            'Clients are responsible for final decisions regarding implementation'
          ]
        }
      ]
    },
    {
      title: 'Limitation of Liability',
      content: [
        {
          text: 'To the fullest extent permitted by law, Z37 shall not be liable for any indirect, incidental, special, consequential, or punitive damages, or any loss of profits or revenues, whether incurred directly or indirectly, or any loss of data, use, goodwill, or other intangible losses resulting from:'
        },
        {
          list: [
            'Your use or inability to use our website or services',
            'Any unauthorized access to or use of our servers',
            'Any interruption or cessation of transmission to or from our website',
            'Any bugs, viruses, or similar harmful code transmitted through our website',
            'Any errors or omissions in any content'
          ]
        }
      ]
    },
    {
      title: 'Indemnification',
      content: [
        {
          text: 'You agree to indemnify and hold harmless Z37 and its officers, directors, employees, and agents from any claims, damages, losses, liabilities, and expenses (including legal fees) arising from your use of the website or violation of these terms.'
        }
      ]
    },
    {
      title: 'Third-Party Links',
      content: [
        {
          text: 'Our website may contain links to third-party websites. We are not responsible for the content, privacy policies, or practices of any third-party websites. Your use of third-party websites is at your own risk.'
        }
      ]
    },
    {
      title: 'Changes to Terms',
      content: [
        {
          text: 'We reserve the right to modify these Terms of Service at any time. Changes will be effective immediately upon posting to the website. Your continued use of the website after any changes indicates your acceptance of the modified terms.'
        }
      ]
    },
    {
      title: 'Governing Law',
      content: [
        {
          text: 'These Terms of Service shall be governed by and construed in accordance with the laws of the State of New York, United States, without regard to its conflict of law provisions.'
        }
      ]
    },
    {
      title: 'Dispute Resolution',
      content: [
        {
          text: 'Any disputes arising from these terms or your use of our website shall be resolved through binding arbitration in accordance with the rules of the American Arbitration Association, conducted in New York, USA.'
        }
      ]
    },
    {
      title: 'Contact Information',
      content: [
        {
          text: 'If you have any questions about these Terms of Service, please contact us at:'
        },
        {
          contact: {
            email: 'hello@z37.io',
            address: 'Z37 LLC, New York, USA'
          }
        }
      ]
    }
  ];

  return (
    <div className="min-h-screen bg-background">
      {/* Hero Section */}
      <section className="relative pt-32 pb-16 overflow-hidden">
        <div className="absolute inset-0 opacity-[0.02]">
          <div 
            className="absolute inset-0" 
            style={{
              backgroundImage: `radial-gradient(circle at 70% 30%, rgba(183, 255, 0, 0.15) 1px, transparent 1px)`,
              backgroundSize: '40px 40px'
            }}
          />
        </div>

        <div className="container relative">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="max-w-3xl mx-auto text-center"
          >
            <Link 
              to="/" 
              className="inline-flex items-center gap-2 text-sm text-secondary hover:text-accent transition-colors mb-8"
            >
              ← Back to Home
            </Link>
            <h1 className="text-4xl md:text-5xl font-display font-bold gradient-text mb-6">
              Terms of Service
            </h1>
            <p className="text-secondary text-lg">
              Last updated: {lastUpdated}
            </p>
          </motion.div>
        </div>
      </section>

      {/* Content Section */}
      <section className="pb-24">
        <div className="container">
          <div className="max-w-3xl mx-auto">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="prose prose-invert prose-lg"
            >
              <p className="text-secondary leading-relaxed mb-12">
                Welcome to Z37. These Terms of Service govern your use of our website and services. 
                Please read them carefully before using our platform.
              </p>

              {sections.map((section, index) => (
                <motion.div
                  key={section.title}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: 0.3 + index * 0.05 }}
                  className="mb-12"
                >
                  <h2 className="text-2xl font-display font-semibold text-primary mb-4">
                    {section.title}
                  </h2>
                  
                  {section.content.map((item, itemIndex) => (
                    <div key={itemIndex} className="mb-4">
                      {item.text && (
                        <p className="text-secondary leading-relaxed">
                          {item.text}
                        </p>
                      )}
                      {item.list && (
                        <ul className="list-disc list-inside text-secondary space-y-2 mt-2">
                          {item.list.map((listItem, listIndex) => (
                            <li key={listIndex}>{listItem}</li>
                          ))}
                        </ul>
                      )}
                      {item.contact && (
                        <div className="bg-white/[0.02] border border-borders rounded-lg p-6 mt-4">
                          <p className="text-secondary">
                            <strong className="text-primary">Email:</strong>{' '}
                            <a 
                              href={`mailto:${item.contact.email}`}
                              className="text-accent hover:text-signal-lime transition-colors"
                            >
                              {item.contact.email}
                            </a>
                          </p>
                          <p className="text-secondary mt-2">
                            <strong className="text-primary">Address:</strong> {item.contact.address}
                          </p>
                        </div>
                      )}
                    </div>
                  ))}
                </motion.div>
              ))}
            </motion.div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default TermsOfService;
